/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { makeAsyncReducer } from 'ui-rating-review/src/utils/async_utils';

const API_URL = `${process.env.API}/inventory`;
export const SLUG_LOCALSTORAGE_KEY = 'REC_GOV_SLUG_CACHE';
const SLUG_CACHE_TTL_IN_DAYS = 7;
export const INVENTORY_FETCH_SLUG_START = 'INVENTORY_FETCH_SLUG_START';
export const INVENTORY_FETCH_SLUG_SUCCESS = 'INVENTORY_FETCH_SLUG_SUCCESS';
export const INVENTORY_FETCH_SLUG_FAILURE = 'INVENTORY_FETCH_SLUG_FAILURE';

function fetchSlugs() {
    return axios.get(`${API_URL}/slugs`);
}

function checkLocalCache() {
    const cache = localStorage.getItem(SLUG_LOCALSTORAGE_KEY);
    return cache ? JSON.parse(cache) : null;
}

function getExpirationString() {
    return gnoment().add(SLUG_CACHE_TTL_IN_DAYS, 'days').format();
}

function isExpired(expires) {
    return !expires || gnoment(expires).isBefore();
}

function saveToLocalCache(slugs, expires) {
    const cache = {
        slugs,
        expires,
    };
    localStorage.setItem(SLUG_LOCALSTORAGE_KEY, JSON.stringify(cache));
}

function slugLoadStartAction() {
    return { type: INVENTORY_FETCH_SLUG_START };
}

function slugLoadSuccessAction(slugs, expires) {
    return { type: INVENTORY_FETCH_SLUG_SUCCESS, payload: { slugs, expires } };
}

function slugLoadFailureAction(error) {
    return { type: INVENTORY_FETCH_SLUG_FAILURE, error };
}

export const slugsSelector = (state) => state.inventorySlugs;

export function getLocationFromSlug(slug, state) {
    const { loaded, data } = slugsSelector(state);
    if (!slug || !loaded || isEmpty(data?.slugs[slug])) {
        return null;
    }
    const { location_id, location_type } = data?.slugs[slug];
    return { locationId: location_id, locationType: location_type };
}

export function hydrateSlugs() {
    return async (dispatch, getState) => {
        // Check if slugs already hydrated
        const reduxState = slugsSelector(getState());
        if (!isExpired(reduxState?.data?.expires)) {
            return;
        }
        dispatch(slugLoadStartAction());
        const cache = checkLocalCache();
        if (!isExpired(cache?.expires)) {
            dispatch(slugLoadSuccessAction(cache.slugs, cache.expires));
            return;
        }
        try {
            const response = await fetchSlugs();
            const expires = getExpirationString();
            saveToLocalCache(response.data, expires);
            dispatch(slugLoadSuccessAction(response.data, expires));
        } catch (e) {
            dispatch(slugLoadFailureAction(e));
        }
    };
}

export function useSlugLookup(slug) {
    const dispatch = useDispatch();
    const { loading, loaded, data } = useSelector(slugsSelector);

    if (!loading && isExpired(data?.expires)) {
        dispatch(hydrateSlugs());
    }

    const slugData = data?.slugs[slug];
    if (isEmpty(slugData)) {
        return { loaded };
    }
    return {
        locationId: slugData.location_id,
        locationType: slugData.location_type,
        loaded,
    };
}

export const inventorySlugReducer = makeAsyncReducer({
    startType: INVENTORY_FETCH_SLUG_START,
    successType: INVENTORY_FETCH_SLUG_SUCCESS,
    failureType: INVENTORY_FETCH_SLUG_FAILURE,
});
